import startCase from 'lodash/startCase';
import { ORIGIN_BACKEND_HEADER } from '@/constants';

const openSearchApiKey =
  process.env.NEXT_PUBLIC_OPEN_SEARCH_API_KEY ||
  'jbS0CPECEy8u1LVZcqgqg4lTYCdy6nJtBkEl4GK2';

class SellTreezError extends Error {
  constructor(
    message: string,
    {
      cause,
    }: { cause: { data: any; status: number; req: any; endpoint: string } },
  ) {
    super(message);
    this.name = this.constructor.name;
    this.cause = cause;
  }
}

export class SellTreezClient {
  private _apiUrl: string;
  private _entityId: string;

  constructor(entityId: string) {
    this._apiUrl = getSellTreezApiUrl();
    this._entityId = entityId;
  }

  private request = async (
    endpoint: string,
    // eslint-disable-next-line no-undef
    { method, body, headers: customHeaders, ...customConfig }: RequestInit,
  ) => {
    const config: RequestInit = {
      body,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        'Entity-Id': this._entityId,
        'X-Api-Key': openSearchApiKey,
        ...(process.env.X_API_ORIGIN_BACKEND
          ? { [ORIGIN_BACKEND_HEADER]: process.env.X_API_ORIGIN_BACKEND }
          : {}),
        ...customHeaders,
      },
      method,
      ...customConfig,
    };

    const response = await fetch(`${this._apiUrl}/${endpoint}`, config).catch(
      (reason): Promise<Response> => Promise.reject(reason),
    );

    const data = await response.json().catch(() => {});

    if (response.ok) {
      return data;
    }

    throw new SellTreezError(
      startCase(
        (data.detail ?? data.description)
          ?.replace(/[_]/g, ' ')
          ?.toLocaleLowerCase(),
      ),
      {
        cause: { data, status: response.status, req: config, endpoint },
      },
    );
  };

  delete = async <T extends Object>(endpoint: string) => {
    const data = await this.request(endpoint, {
      method: 'DELETE',
    });

    return data as T;
  };

  get = async <T extends Object>(endpoint: string, signal?: AbortSignal) => {
    const data = await this.request(endpoint, {
      method: 'GET',
      signal,
    });

    return data as T;
  };

  patch = async <T extends Object>(endpoint: string, resource: Partial<T>) => {
    const data = await this.request(endpoint, {
      body: JSON.stringify(resource),
      method: 'PATCH',
    });

    return data as T;
  };

  post = async <T extends Object>(endpoint: string, resource?: T) => {
    const data = await this.request(endpoint, {
      ...(resource ? { body: JSON.stringify(resource) } : {}),
      method: 'POST',
    });
    return data;
  };

  put = async <T extends Object>(endpoint: string, resource?: T) => {
    const data = await this.request(endpoint, {
      ...(resource ? { body: JSON.stringify(resource) } : {}),
      method: 'PUT',
    });

    return data as T;
  };
}

const getSellTreezApiUrl = () => {
  const endpointUrl = process.env.NEXT_PUBLIC_OPEN_SEARCH_ENDPOINT;
  if (!endpointUrl) {
    throw new Error('getSellTreezApiUrl: endpointUrl is not set');
  }

  const { origin, pathname } = new URL(endpointUrl);
  const firstPathSegment = pathname.split('/').filter(Boolean)[0];

  if (firstPathSegment === 'develop' || firstPathSegment === 'prod') {
    return `${origin}/${firstPathSegment}`;
  }

  return origin;
};
